import type {
  RootfiDataModelV4,
  RootFiWorkspaceSyncFrequencyV4,
} from '@commenda/types';
import { RootFiIntegration } from '@prisma/client';

export interface StripeIntegrationSettings {
  fallbackToPaymentMethodAddressEnabled?: boolean;
}

export interface IntegrationPlatformSettings {
  stripe?: StripeIntegrationSettings;
}

/** One data model Commenda syncs for this connection, as configured in RootFi. */
export interface IntegrationDataModelSyncSettings {
  dataModel: RootfiDataModelV4;
  label: string;
  frequency: RootFiWorkspaceSyncFrequencyV4;
  interval: number;
  /** ISO date (YYYY-MM-DD) RootFi pulls history from; null = all history. */
  syncFrom: string | null;
}

export interface UpdateDataModelSyncSettings {
  dataModel: RootfiDataModelV4;
  frequency: RootFiWorkspaceSyncFrequencyV4;
  /** Omit to keep the current value. */
  interval?: number;
  /** Omit to keep the current value; null clears it. */
  syncFrom?: string | null;
}

export interface UpdateSettingsPayload {
  rootfiCompanyId: number;
  corporationId: number;
  taxCalculationEnabled?: boolean;
  taxCalculationStartDate?: string;
  syncEnabled?: boolean;
  platformSettings?: IntegrationPlatformSettings;
  dataModelSyncSettings?: UpdateDataModelSyncSettings[];
}

export interface UpdateSettingsResponse {
  id: number;
  corporation_id: number;
  tax_calculation_enabled: boolean;
  tax_calculation_start_date: string | null;
}

export interface IntegrationSettings {
  id: number;
  syncEnabled: boolean;
  taxCalculationEnabled: boolean;
  taxCalculationStartDate: string | null;
  taxCalculationBlockedByOtherConnection: boolean;
  corporationId: number;
  integration: RootFiIntegration;
  platformSettings?: IntegrationPlatformSettings;
  /** Null when the connection is not on RootFi Core v4 or not connected. */
  dataModelSyncSettings: IntegrationDataModelSyncSettings[] | null;
}

export enum RootfiSettingsFieldType {
  BOOLEAN = 'boolean',
  DATE = 'date',
  SYNC_FREQUENCY = 'sync-frequency',
}

export interface RootfiSettingsInitialHistoryChange {
  kind: 'initial';
  key: string;
  label: string;
}

export interface RootfiSettingsFieldHistoryChange {
  kind: 'field-change';
  key: string;
  label: string;
  previousValue: string;
  nextValue: string;
  type: RootfiSettingsFieldType;
  /** Shown in place of an empty value. */
  emptyValueLabel?: string;
}

export type RootfiSettingsHistoryChange =
  | RootfiSettingsInitialHistoryChange
  | RootfiSettingsFieldHistoryChange;

export interface RootfiSettingsHistoryItem {
  id: string;
  occurredAt: string;
  actor: {
    id: string;
    name: string | null;
    email: string | null;
    image: string | null;
    role: string;
  };
  changes: RootfiSettingsHistoryChange[];
}

export interface RootfiSettingsHistoryResponse {
  history: RootfiSettingsHistoryItem[];
  count: number;
}
